<template>
  <div class="profile-page">
    <a href="#main-content" class="skip-link">Skip to main content</a>
    <div v-if="loading" class="loading" role="status" aria-live="polite">
      <span aria-hidden="true">Loading profile...</span>
      <span class="visually-hidden">Loading user profile information</span>
    </div>

    <div v-else-if="profile" class="profile-container">
      <!-- Hero Banner -->
      <header class="profile-hero-banner" role="banner" :style="{
        backgroundImage: profile.profileBackgroundURL
          ? `url(${profile.profileBackgroundURL})`
          : `url(${defaultBanner})`,
        backgroundSize: `${(profile.backgroundImageZoom || 1) * 100}%`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: (profile.backgroundImagePositionX !== undefined || profile.backgroundImagePositionY !== undefined)
          ? `${profile.backgroundImagePositionX || 50}% ${profile.backgroundImagePositionY || 50}%`
          : 'center',
        backgroundColor: '#ffffff'
      }">
        <div class="hero-overlay"></div>

        <nav class="profile-navigation" aria-label="Profile navigation">
          <button @click="handleBackNavigation" class="back-to-gallery-btn" :aria-label="isOwner ? 'Exit preview and return to dashboard' : 'Return to artist gallery'">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            {{ isOwner ? 'Exit' : 'Gallery' }}
          </button>

          <!-- Profile View Switch Button (for "both" users) -->
          <button
            v-if="canSwitchView"
            @click="switchProfileView"
            class="switch-profile-btn"
            :class="alternateView === 'scientist' ? 'to-scientist' : 'to-artist'"
            :aria-label="`Switch to ${alternateView} profile view`"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"/>
            </svg>
            {{ alternateView === 'scientist' ? 'Science Profile' : 'Artist Profile' }}
          </button>
        </nav>

        <!-- Commission Status Indicator -->
        <div v-if="(profile.openToCommissions === true) || (profile.openToCollaborations === true)" class="commission-indicator" role="status" aria-label="Commission status">
          <span class="commission-dot" aria-hidden="true"></span>
          <span class="commission-text">Seeking Collaborations</span>
        </div>
      </header>

      <!-- Profile Content -->
      <main id="main-content" class="profile-content">
        <section class="profile-main" aria-labelledby="profile-name">
          <!-- Left side: Profile Image and Tags -->
          <div class="profile-left-section">
            <div class="profile-image-section">
              <div class="profile-image-container" :style="getProfileImageContainerStyle()">
                <div v-if="profile.profilePictureURL"
                  :style="getProfileImageStyle()"
                  :aria-label="`${getDisplayName()}'s profile picture`"
                  role="img"
                  class="profile-image" />
                <svg v-else
                  class="default-profile-svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  role="img"
                  :aria-label="`Default profile picture for ${getDisplayName()}`"
                >
                  <circle cx="12" cy="7" r="5" stroke="#C1C1C1" stroke-width="2"/>
                  <path d="M17 14H17.3517C18.8646 14 20.1408 15.1266 20.3285 16.6279L20.719 19.7519C20.8682 20.9456 19.9374 22 18.7344 22H5.26556C4.06257 22 3.1318 20.9456 3.28101 19.7519L3.67151 16.6279C3.85917 15.1266 5.13538 14 6.64835 14H7" stroke="#C1C1C1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>

            <!-- Tags Section underneath image -->
            <section class="profile-tags" aria-labelledby="expertise-heading">
              <h4 id="expertise-heading">Expertise/Areas of Interest</h4>
              <!-- Expertise Tags -->
              <div v-if="profile.tagsSelected && profile.tagsSelected.length" class="tag-group" role="list" aria-label="Expertise tags">
                <span v-for="tag in profile.tagsSelected" :key="tag"
                      class="expertise-tag"
                      :class="getTagThemeClass(tag)"
                      role="listitem">
                  {{ tag }}
                </span>
              </div>
              <p v-else class="placeholder-text">{{ getExpertisePlaceholder() }}</p>
            </section>
          </div>

          <!-- Right side: Name and Bio -->
          <div class="profile-text-section">
            <!-- Profile Info -->
            <section class="profile-info" aria-labelledby="profile-name">
              <div class="name-institution-row">
                <h1 id="profile-name" class="profile-name" :class="getProfileThemeClass()">
                  {{ getDisplayName() }}
                  <span v-if="isAdmin && !isVerified" class="unverified-badge" aria-label="Account not verified">Unverified</span>
                </h1>
                <!-- Institution badge for scientists (uses viewMode for both users) -->
                <span v-if="profile.institution && viewMode === 'scientist'" class="institution-badge scientist-badge">
                  {{ profile.institution }}
                </span>
                <!-- Organization badge for artists (uses viewMode for both users) -->
                <span v-if="profile.artistOrganization && viewMode === 'artist'" class="institution-badge artist-badge">
                  {{ profile.artistOrganization }}
                </span>
              </div>
              <p class="profile-bio">{{ getBioText() }}</p>

              <!-- Social/Professional Links -->
              <section class="social-links-section" aria-labelledby="connect-heading">
                <h3 id="connect-heading">Connect</h3>
                <div v-if="hasSocialLinks || hasProfessionalLinks" class="social-links" role="list">
                  <!-- Artist Social Links -->
                  <a v-if="profile.socialLinks?.portfolio" :href="formatUrl(profile.socialLinks.portfolio)" target="_blank" rel="noopener noreferrer" class="social-link" role="listitem" :aria-label="`Visit ${getDisplayName()}'s portfolio (opens in new tab)`">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                    </svg>
                    Portfolio
                    <span class="visually-hidden">(opens in new tab)</span>
                  </a>
                  <a v-if="profile.socialLinks?.instagram" :href="formatSocialUrl('instagram', profile.socialLinks.instagram)" target="_blank" rel="noopener noreferrer" class="social-link" role="listitem" :aria-label="`Follow ${getDisplayName()} on Instagram (opens in new tab)`">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                      <path d="M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-1.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8378.6165 19.074.321 18.2017.1197 16.9244.0645 15.6471.0093 15.236-.005 11.977.0014 8.718.0076 8.31.0215 7.0301.0839m.1402 21.6932c-1.17-.0509-1.8053-.2453-2.2287-.408-.5606-.216-.96-.4771-1.3819-.895-.422-.4178-.6811-.8186-.9-1.378-.1644-.4234-.3624-1.058-.4171-2.228-.0595-1.2645-.072-1.6442-.079-4.848-.007-3.2037.0053-3.583.0607-4.848.05-1.169.2456-1.805.408-2.2282.216-.5613.4762-.96.895-1.3816.4188-.4217.8184-.6814 1.3783-.9003.423-.1651 1.0575-.3614 2.227-.4171 1.2655-.06 1.6447-.072 4.848-.079 3.2033-.007 3.5835.005 4.8495.0608 1.169.0508 1.8053.2445 2.228.408.5608.216.96.4754 1.3816.895.4217.4194.6816.8176.9005 1.3787.1653.4217.3617 1.056.4169 2.2263.0602 1.2655.0739 1.645.0796 4.848.0058 3.203-.0055 3.5834-.061 4.848-.051 1.17-.245 1.8055-.408 2.2294-.216.5604-.4763.96-.8954 1.3814-.419.4215-.8181.6811-1.3783.9-.4224.1649-1.0577.3617-2.2262.4174-1.2656.0595-1.6448.072-4.8493.079-3.2045.007-3.5825-.006-4.848-.0608M16.953 5.5864A1.44 1.44 0 1 0 18.39 4.144a1.44 1.44 0 0 0-1.437 1.4424M5.8385 12.012c.0067 3.4032 2.7706 6.1557 6.173 6.1493 3.4026-.0065 6.157-2.7701 6.1506-6.1733-.0065-3.4032-2.771-6.1565-6.174-6.1498-3.403.0067-6.156 2.771-6.1496 6.1738M8 12.0077a4 4 0 1 1 4.008 3.9921A3.9996 3.9996 0 0 1 8 12.0077"/>
                    </svg>
                    Instagram
                    <span class="visually-hidden">(opens in new tab)</span>
                  </a>
                  <a v-if="profile.socialLinks?.twitter" :href="formatSocialUrl('twitter', profile.socialLinks.twitter)" target="_blank" rel="noopener noreferrer" class="social-link" role="listitem" :aria-label="`Follow ${getDisplayName()} on X (opens in new tab)`">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                      <path d="M14.234 10.162 22.977 0h-2.072l-7.591 8.824L7.251 0H.258l9.168 13.343L.258 24H2.33l8.016-9.318L16.749 24h6.993zm-2.837 3.299-.929-1.329L3.076 1.56h3.182l5.965 8.532.929 1.329 7.754 11.09h-3.182z"/>
                    </svg>
                    X
                    <span class="visually-hidden">(opens in new tab)</span>
                  </a>
                  <a v-if="profile.socialLinks?.other" :href="formatUrl(profile.socialLinks.other)" target="_blank" rel="noopener noreferrer" class="social-link" role="listitem" :aria-label="`Visit ${getDisplayName()}'s external link (opens in new tab)`">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.72"/>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.72"/>
                    </svg>
                    Link
                    <span class="visually-hidden">(opens in new tab)</span>
                  </a>
                  
                  <!-- Scientist Professional Links -->
                  <a v-if="profile.professionalLinks?.orcid" :href="formatUrl(profile.professionalLinks.orcid)" target="_blank" rel="noopener noreferrer" class="social-link" role="listitem" :aria-label="`Visit ${getDisplayName()}'s ORCID profile (opens in new tab)`">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                      <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zM7.369 4.378c.525 0 .947.431.947.947s-.422.947-.947.947a.95.95 0 0 1-.947-.947c0-.525.422-.947.947-.947zm-.722 3.038h1.444v10.041H6.647V7.416zm3.562 0h3.9c3.712 0 5.344 2.653 5.344 5.025 0 2.578-2.016 5.025-5.325 5.025h-3.919V7.416zm1.444 1.303v7.444h2.297c3.272 0 4.022-2.484 4.022-3.722 0-2.016-1.284-3.722-4.097-3.722h-2.222z"/>
                    </svg>
                    ORCID
                    <span class="visually-hidden">(opens in new tab)</span>
                  </a>
                  <a v-if="profile.professionalLinks?.googleScholar" :href="formatUrl(profile.professionalLinks.googleScholar)" target="_blank" rel="noopener noreferrer" class="social-link" role="listitem" :aria-label="`Visit ${getDisplayName()}'s Google Scholar profile (opens in new tab)`">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                      <path d="M5.242 13.769L0 9.5 12 0l12 9.5-5.242 4.269C17.548 11.249 14.978 9.5 12 9.5c-2.977 0-5.548 1.748-6.758 4.269zM12 10a7 7 0 1 0 0 14 7 7 0 0 0 0-14z"/>
                    </svg>
                    Scholar
                    <span class="visually-hidden">(opens in new tab)</span>
                  </a>
                  <a v-if="profile.professionalLinks?.linkedin" :href="formatUrl(profile.professionalLinks.linkedin)" target="_blank" rel="noopener noreferrer" class="social-link" role="listitem" :aria-label="`Visit ${getDisplayName()}'s LinkedIn profile (opens in new tab)`">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn
                    <span class="visually-hidden">(opens in new tab)</span>
                  </a>
                  <a v-if="profile.professionalLinks?.other" :href="formatUrl(profile.professionalLinks.other)" target="_blank" rel="noopener noreferrer" class="social-link" role="listitem" :aria-label="`Visit ${getDisplayName()}'s professional link (opens in new tab)`">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.72"/>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.72-1.72"/>
                    </svg>
                    Professional Link
                    <span class="visually-hidden">(opens in new tab)</span>
                  </a>
                </div>
                <p v-else class="placeholder-text">{{ getSocialLinksPlaceholder() }}</p>
              </section>

              <!-- Publications Section (for scientists) - uses viewMode for both users -->
              <section class="publications-inline-section scientist-section" v-if="viewMode === 'scientist' && profile.publications && profile.publications.trim()" aria-labelledby="publications-inline-heading">
                <h3 id="publications-inline-heading">Publications & Research</h3>
                <p class="publications-text">{{ profile.publications }}</p>
              </section>

              <!-- Awards Section (for artists) - uses viewMode for both users -->
              <section class="publications-inline-section artist-section" v-if="viewMode === 'artist' && profile.artistAwards && profile.artistAwards.trim()" aria-labelledby="awards-inline-heading">
                <h3 id="awards-inline-heading">Awards & Notable Work</h3>
                <p class="publications-text">{{ profile.artistAwards }}</p>
              </section>
            </section>
          </div>
        </section>
      </main>


      <!-- Portfolio Section -->
      <div class="section-divider"></div>
      <section class="portfolio-section" aria-labelledby="portfolio-heading">
        <div class="portfolio-header">
          <h2 id="portfolio-heading" class="section-heading">Portfolio</h2>
          <p v-if="profile.mission" class="portfolio-description">{{ profile.mission }}</p>
        </div>

        <div v-if="profile.portfolio && profile.portfolio.length > 0" class="portfolio-showcase">
          <div class="portfolio-main" v-if="currentPortfolioItem">
            <div class="portfolio-image-section">
              <div class="portfolio-main-image"
                   role="img"
                   :aria-label="`Portfolio image: ${currentPortfolioItem.title || 'Untitled'}`"
                   :style="{
                     backgroundColor: '#ffffff',
                     backgroundImage: `url(${currentPortfolioItem.imageUrl})`,
                     backgroundSize: `${(currentPortfolioItem.zoom || 1) * 100}%`,
                     backgroundPosition: `${currentPortfolioItem.positionX || 50}% ${currentPortfolioItem.positionY || 50}%`
                   }">
              </div>
            </div>

            <div class="portfolio-details">
              <div class="portfolio-text">
                <h3 v-if="currentPortfolioItem.title" class="portfolio-title">
                  {{ currentPortfolioItem.title }}
                </h3>
                <p v-if="currentPortfolioItem.caption" class="portfolio-description">
                  {{ currentPortfolioItem.caption }}
                </p>
              </div>
              <!-- Image Counter - subtle page indicator -->
              <div v-if="profile.portfolio.length > 1" id="portfolio-counter" class="image-counter" role="status" aria-live="polite">
                <span class="visually-hidden">Image </span>{{ currentPortfolioIndex + 1 }} / {{ profile.portfolio.length }}
              </div>
            </div>
          </div>

          <!-- Portfolio Thumbnails with Navigation -->
          <div class="portfolio-thumbnails-wrapper" v-if="profile.portfolio.length > 1">
            <button
              @click="previousImage"
              class="carousel-nav carousel-prev"
              aria-label="Previous portfolio image"
              :aria-describedby="'portfolio-counter'">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>

            <div class="portfolio-thumbnails" role="tablist" aria-label="Portfolio image thumbnails">
              <button
                v-for="(item, index) in profile.portfolio"
                :key="item.id || index"
                class="portfolio-thumbnail"
                :class="{ active: index === currentPortfolioIndex }"
                @click="selectThumbnail(index)"
                role="tab"
                :aria-selected="index === currentPortfolioIndex"
                :aria-label="`View portfolio image ${index + 1}: ${item.title || 'Untitled'}`"
                :tabindex="index === currentPortfolioIndex ? 0 : -1"
              >
                <div class="thumbnail-image"
                     :style="{
                       backgroundColor: '#ffffff',
                       backgroundImage: `url(${item.imageUrl})`,
                       backgroundSize: `${(item.zoom || 1) * 100}%`,
                       backgroundPosition: `${item.positionX || 50}% ${item.positionY || 50}%`
                     }"
                     aria-hidden="true"></div>
              </button>
            </div>

            <button
              @click="nextImage"
              class="carousel-nav carousel-next"
              aria-label="Next portfolio image"
              :aria-describedby="'portfolio-counter'">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        </div>
        <div v-else class="portfolio-placeholder">
          <p class="placeholder-text">{{ getPortfolioPlaceholder() }}</p>
        </div>
      </section>

    </div>

    <div v-else class="error-state" role="alert">
      <h2>Profile not found</h2>
      <p>This profile may be private or no longer exist.</p>
      <button @click="goToGallery" class="back-btn" aria-label="Return to artist gallery">Return to Gallery</button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { auth, db } from '@/firebase.js'
import { doc, getDoc } from 'firebase/firestore'
import { onAuthStateChanged } from 'firebase/auth'
import { getTagTheme } from '../config/tags.js'

// Import default images
import defaultProfile from '@/assets/default_profile.jpg'
import defaultBanner from '@/assets/default_banner.jpg'

export default {
  name: 'ProfilePageNew',
  setup() {
    const route = useRoute()
    const router = useRouter()
    
    const loading = ref(true)
    const profile = ref(null)
    const currentUser = ref(null)
    const currentPortfolioIndex = ref(0)
    const autoplayInterval = ref(null)
    const autoplayActive = ref(true)
    
    const isOwner = computed(() => {
      return currentUser.value && profile.value && currentUser.value.uid === profile.value.uid
    })
    
    const isAdmin = computed(() => {
      return currentUser.value?.customClaims?.admin === true
    })
    
    const isVerified = computed(() => {
      return profile.value?.verified === true
    })

    // View mode for "both" users - allows viewing as artist or scientist
    const viewMode = computed(() => {
      if (!profile.value) return null
      // For single-role users, always show their role
      if (profile.value.role !== 'both') return profile.value.role
      // For "both" users, check query parameter (default to 'artist')
      return route.query.view === 'scientist' ? 'scientist' : 'artist'
    })

    // Whether this is a "both" user who can switch views
    const canSwitchView = computed(() => {
      return profile.value?.role === 'both'
    })

    // Get the alternate view for switching
    const alternateView = computed(() => {
      return viewMode.value === 'artist' ? 'scientist' : 'artist'
    })

    // Switch to the other profile view
    const switchProfileView = () => {
      router.push({
        path: route.path,
        query: { view: alternateView.value }
      })
    }


    const hasSocialLinks = computed(() => {
      return profile.value?.socialLinks && (
        profile.value.socialLinks.portfolio ||
        profile.value.socialLinks.instagram ||
        profile.value.socialLinks.twitter ||
        profile.value.socialLinks.other
      )
    })

    const hasProfessionalLinks = computed(() => {
      return profile.value?.professionalLinks && (
        profile.value.professionalLinks.orcid ||
        profile.value.professionalLinks.googleScholar ||
        profile.value.professionalLinks.linkedin ||
        profile.value.professionalLinks.other
      )
    })

    const currentPortfolioItem = computed(() => {
      if (!profile.value?.portfolio?.length) return null
      return profile.value.portfolio[currentPortfolioIndex.value] || profile.value.portfolio[0]
    })

    // Theme-based styling - uses viewMode for "both" users
    const getProfileThemeClass = () => {
      if (!profile.value) return 'artist-theme'
      // Use viewMode instead of raw role for "both" users
      const effectiveRole = viewMode.value || profile.value.role
      return effectiveRole === 'scientist' ? 'scientist-theme' : 'artist-theme'
    }

    const getTagThemeClass = (tagName) => {
      const theme = getTagTheme(tagName);
      return theme === 'art' ? 'artist-theme' : theme === 'science' ? 'scientist-theme' : 'both-theme';
    }

    const getDisplayName = () => {
      if (!profile.value) return 'User'
      const firstName = profile.value.firstName || ''
      const lastName = profile.value.lastName || ''
      const fullName = `${firstName} ${lastName}`.trim()
      return fullName || profile.value.name || 'User'
    }


    const getProfileImageStyle = () => {
      if (!profile.value || !profile.value.profilePictureURL) {
        return {
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          backgroundColor: '#f0f0f0'
        }
      }
      
      const zoom = profile.value.profileImageZoom || 1
      const posX = profile.value.profileImagePositionX !== undefined ? profile.value.profileImagePositionX : 50
      const posY = profile.value.profileImagePositionY !== undefined ? profile.value.profileImagePositionY : 50
      
      return {
        width: '300px',
        height: '300px',
        borderRadius: '50%', // Always circular for profiles
        backgroundColor: '#ffffff', // White background for transparent PNGs
        backgroundImage: `url(${profile.value.profilePictureURL})`,
        backgroundSize: `${zoom * 100}%`,
        backgroundPosition: `${posX}% ${posY}%`,
        backgroundRepeat: 'no-repeat'
      }
    }


    const getProfileImageContainerStyle = () => {
      return {
        borderRadius: '50%'
      }
    }

    const getBioText = () => {
      if (!profile.value) return getDefaultBio()
      
      // For scientists, prioritize researchBio over bio
      if (profile.value.role === 'scientist') {
        return profile.value.researchBio || profile.value.bio || getDefaultBio()
      }
      
      // For artists and others, use bio field
      return profile.value.bio || profile.value.researchBio || getDefaultBio()
    }

    const getDefaultBio = () => {
      if (!profile.value) return 'PALLAS Community Member'
      return profile.value.role === 'scientist' ? 'Scientist' : 
             profile.value.role === 'both' ? 'Artist & Scientist' : 'Artist'
    }

    const getCommissionText = () => {
      if (!profile.value) return 'Available for Work'
      return profile.value.role === 'scientist' ?
        (profile.value.openToCollaborations === true ? 'Open for Collaborations' : 'Available for Work') :
        (profile.value.openToCommissions === true ? 'Open for Commissions' : 'Available for Work')
    }

    // Role-specific placeholder text generators
    const getMissionPlaceholder = () => {
      if (!profile.value) return 'Share your goals, values, and the impact you want to create.'
      switch (profile.value.role) {
        case 'scientist':
          return 'Share your research goals, scientific values, and the impact you want to create through your work.'
        case 'artist':
          return 'Share your artistic goals, creative values, and the impact you want to create through your work.'
        case 'both':
          return 'Share your goals, values, and the impact you want to create through your work.'
        default:
          return 'Share your goals, values, and the impact you want to create.'
      }
    }

    const getSocialLinksPlaceholder = () => {
      if (!profile.value) return 'Connect through social media and portfolio links.'
      switch (profile.value.role) {
        case 'scientist':
          return 'Connect with this researcher through their professional and social media links.'
        case 'artist':
          return 'Connect with this artist through their social media and portfolio links.'
        case 'both':
          return 'Connect through social media and portfolio links.'
        default:
          return 'Connect through social media and portfolio links.'
      }
    }

    const getExpertisePlaceholder = () => {
      if (!profile.value) return 'No expertise tags selected yet.'
      switch (profile.value.role) {
        case 'scientist':
          return 'No research specializations selected yet.'
        case 'artist':
          return 'No artistic expertise tags selected yet.'
        case 'both':
          return 'No interdisciplinary expertise tags selected yet.'
        default:
          return 'No expertise tags selected yet.'
      }
    }

    const getPortfolioPlaceholder = () => {
      if (!profile.value) return 'Portfolio will be showcased here. Check back soon to see their latest work.'
      switch (profile.value.role) {
        case 'scientist':
          return 'Research portfolio will be showcased here. Check back soon to see their latest projects and publications.'
        case 'artist':
          return 'Artistic portfolio will be showcased here. Check back soon to see their latest creative work.'
        case 'both':
          return 'Creative research portfolio will be showcased here. Check back soon to see their latest interdisciplinary work.'
        default:
          return 'Portfolio will be showcased here. Check back soon to see their latest work.'
      }
    }


    const getPublicationsPlaceholder = () => {
      if (!profile.value) return 'Research publications and academic work will be displayed here.'
      switch (profile.value.role) {
        case 'scientist':
          return 'Research publications, papers, and academic work will be displayed here.'
        case 'both':
          return 'Research publications, creative works, and interdisciplinary projects will be displayed here.'
        default:
          return 'Research publications and academic work will be displayed here.'
      }
    }


    // Navigation methods
    const goToGallery = () => {
      router.push('/SearchArtists')
    }

    const handleBackNavigation = () => {
      if (isOwner.value) {
        router.push('/profile-dashboard')
      } else {
        router.push('/SearchArtists')
      }
    }

    const goToSearch = (searchTerm, searchType) => {
      router.push({
        path: '/SearchArtists',
        query: {
          search: searchTerm,
          type: searchType
        }
      })
    }

    // Carousel autoplay functions
    const startAutoplay = () => {
      if (!profile.value?.portfolio?.length || profile.value.portfolio.length <= 1) return
      if (autoplayInterval.value) return // Already running

      autoplayInterval.value = setInterval(() => {
        if (!autoplayActive.value) return
        const length = profile.value.portfolio.length
        currentPortfolioIndex.value = currentPortfolioIndex.value < length - 1
          ? currentPortfolioIndex.value + 1
          : 0
      }, 4000) // 4 seconds per slide
    }

    const stopAutoplay = () => {
      autoplayActive.value = false
      if (autoplayInterval.value) {
        clearInterval(autoplayInterval.value)
        autoplayInterval.value = null
      }
    }

    // Carousel navigation with loop-around (stops autoplay on user interaction)
    const previousImage = () => {
      stopAutoplay()
      if (!profile.value?.portfolio?.length) return
      const length = profile.value.portfolio.length
      currentPortfolioIndex.value = currentPortfolioIndex.value > 0
        ? currentPortfolioIndex.value - 1
        : length - 1
    }

    const nextImage = () => {
      stopAutoplay()
      if (!profile.value?.portfolio?.length) return
      const length = profile.value.portfolio.length
      currentPortfolioIndex.value = currentPortfolioIndex.value < length - 1
        ? currentPortfolioIndex.value + 1
        : 0
    }

    const selectThumbnail = (index) => {
      stopAutoplay()
      currentPortfolioIndex.value = index
    }

    // URL formatting helpers
    const formatWebsiteUrl = (url) => {
      if (!url) return ''
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url
      }
      return 'https://' + url
    }

    const formatWebsiteDisplay = (url) => {
      if (!url) return ''
      return url.replace(/^https?:\/\//, '').replace(/\/$/, '')
    }

    const formatUrl = (url) => {
      if (!url) return ''
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url
      }
      return 'https://' + url
    }

    const formatSocialUrl = (platform, handle) => {
      if (!handle) return ''
      const cleanHandle = handle.replace('@', '')

      switch (platform) {
        case 'instagram':
          return `https://instagram.com/${cleanHandle}`
        case 'twitter':
          return `https://twitter.com/${cleanHandle}`
        default:
          return handle
      }
    }

    // Load profile data
    const loadProfile = async () => {
      try {
        loading.value = true
        const profileId = route.params.uuid
        
        if (!profileId) {
          console.error('No profile ID provided')
          return
        }

        const profileDoc = await getDoc(doc(db, 'users', profileId))
        
        if (profileDoc.exists()) {
          const profileData = profileDoc.data()

          // Determine which profile view to show for "both" users
          const viewParam = route.query.view
          const userRole = profileData.role
          const isScientistView = userRole === 'scientist' || (userRole === 'both' && viewParam === 'scientist')

          // Set effective profile data based on role/view
          // For scientist view, use scientist-specific fields; otherwise use artist fields
          const effectiveData = {
            firstName: isScientistView
              ? (profileData.scientistFirstName || '')
              : (profileData.firstName || ''),
            lastName: isScientistView
              ? (profileData.scientistLastName || '')
              : (profileData.lastName || ''),
            portfolio: isScientistView
              ? (profileData.scientistPortfolio || [])
              : (profileData.portfolio || []),
            tagsSelected: isScientistView
              ? (profileData.scientistTags || [])
              : (profileData.tagsSelected || []),
            profileBackgroundURL: isScientistView
              ? (profileData.scientistBackgroundURL || '')
              : (profileData.profileBackgroundURL || ''),
            backgroundImageZoom: isScientistView
              ? (profileData.scientistBackgroundZoom || 1)
              : (profileData.backgroundImageZoom || 1),
            backgroundImagePositionX: isScientistView
              ? (profileData.scientistBackgroundPositionX || 50)
              : (profileData.backgroundImagePositionX || 50),
            backgroundImagePositionY: isScientistView
              ? (profileData.scientistBackgroundPositionY || 50)
              : (profileData.backgroundImagePositionY || 50),
            profilePictureURL: isScientistView
              ? (profileData.scientistProfilePictureURL || '')
              : (profileData.profilePictureURL || ''),
            profileImageZoom: isScientistView
              ? (profileData.scientistProfileImageZoom || 1)
              : (profileData.profileImageZoom || 1),
            profileImagePositionX: isScientistView
              ? (profileData.scientistProfileImagePositionX || 50)
              : (profileData.profileImagePositionX || 50),
            profileImagePositionY: isScientistView
              ? (profileData.scientistProfileImagePositionY || 50)
              : (profileData.profileImagePositionY || 50)
          }

          profile.value = {
            ...profileData,
            uid: profileDoc.id,
            ...effectiveData
          }

          // Initialize portfolio index if portfolio exists
          if (profile.value.portfolio?.length > 0) {
            currentPortfolioIndex.value = 0
          }
        } else {
          console.error('Profile not found')
          profile.value = null
        }
      } catch (error) {
        console.error('Error loading profile:', error)
        profile.value = null
      } finally {
        loading.value = false
      }
    }

    // Auth state monitoring
    const setupAuth = () => {
      onAuthStateChanged(auth, (user) => {
        currentUser.value = user
      })
    }

    // Lifecycle
    onMounted(async () => {
      setupAuth()
      await loadProfile()
      // Start autoplay after profile loads
      if (profile.value?.portfolio?.length > 1) {
        startAutoplay()
      }
    })

    onUnmounted(() => {
      // Clean up autoplay interval
      stopAutoplay()
    })

    // Watch route changes
    watch(() => route.params.uuid, () => {
      if (route.params.uuid) {
        stopAutoplay()
        autoplayActive.value = true
        loadProfile().then(() => {
          if (profile.value?.portfolio?.length > 1) {
            startAutoplay()
          }
        })
      }
    })

    return {
      loading,
      profile,
      currentUser,
      currentPortfolioIndex,
      currentPortfolioItem,
      isOwner,
      isAdmin,
      isVerified,
      viewMode,
      canSwitchView,
      alternateView,
      switchProfileView,
      defaultProfile,
      defaultBanner,
      getDisplayName,
      getProfileImageStyle,
      getProfileImageContainerStyle,
      getBioText,
      getProfileThemeClass,
      getTagThemeClass,
      getDefaultBio,
      getCommissionText,
      getMissionPlaceholder,
      getSocialLinksPlaceholder,
      getExpertisePlaceholder,
      getPortfolioPlaceholder,
      getPublicationsPlaceholder,
      goToGallery,
      handleBackNavigation,
      goToSearch,
      previousImage,
      nextImage,
      selectThumbnail,
      formatWebsiteUrl,
      formatWebsiteDisplay,
      formatUrl,
      formatSocialUrl,
      hasSocialLinks,
      hasProfessionalLinks
    }
  }
}
</script>

<style scoped>
.profile-page {
  min-height: 100vh;
  background: transparent;
  padding-top: var(--navbarHeight);
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  font-size: 18px;
  color: #666;
}

.error-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 50vh;
  text-align: center;
}

.profile-container {
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  min-height: 100vh;
}

/* Hero Banner */
.profile-hero-banner {
  position: relative;
  height: 400px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 100%);
}

.profile-navigation {
  position: absolute;
  top: calc(var(--navbarHeight) - 20px);
  left: 20px;
  right: 20px;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.back-to-gallery-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 8px;
  color: #333;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.back-to-gallery-btn:hover {
  background: rgba(255, 255, 255, 1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Profile View Switch Button */
.switch-profile-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.switch-profile-btn.to-scientist {
  color: #333333;
}

.switch-profile-btn.to-scientist svg {
  stroke: #6566be;
}

.switch-profile-btn.to-scientist:hover {
  background: rgba(255, 255, 255, 1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(101, 102, 190, 0.3);
}

.switch-profile-btn.to-artist {
  color: #333333;
}

.switch-profile-btn.to-artist svg {
  stroke: var(--artColor);
}

.switch-profile-btn.to-artist:hover {
  background: rgba(255, 255, 255, 1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(248, 177, 155, 0.3);
}

/* Commission Indicator on Banner */
.commission-indicator {
  position: absolute;
  top: calc(var(--navbarHeight) + 30px);
  right: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #f8b19b, #6566be);
  border-radius: 20px;
  z-index: 50;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  color: white;
  font-weight: 500;
}

.commission-dot {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
  will-change: transform, opacity;
}

.commission-text {
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

@keyframes pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .commission-dot {
    animation: none;
  }
}

/* Profile Content */
.profile-content {
  padding: 40px 40px 0;
  position: relative;
}

.profile-main {
  display: flex;
  gap: 30px;
  align-items: flex-start;
  margin-bottom: 0;
}

/* Profile Left Section */
.profile-left-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 300px;  /* Match image width */
  flex-shrink: 0;
}

/* Profile Image */
.profile-image-section {
  margin-top: 20px;  /* Moved down significantly */
  z-index: 2;
}

.profile-image-container {
  position: relative;
  width: 300px;
  height: 300px;
  /* border-radius: 50%; REMOVED - now set dynamically via :style */
  overflow: hidden;
}

.profile-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.default-profile-svg {
  width: 100%;
  height: 100%;
  background: #f8f9fa;
}

/* Profile Text Section */
.profile-text-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 0;
}

/* Profile Info */
.profile-info {
  text-align: left;  /* Left-align all profile info */
}

.name-institution-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 8px;
}

.profile-name {
  font-size: 72px;
  font-weight: 700;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  line-height: 1.1;
  color: #333;
}

.institution-badge {
  font-size: 1rem;
  font-weight: 400;
  color: #888;
}

.unverified-badge {
  background: #ff6b6b;
  color: white;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.profile-bio {
  font-size: 16px;
  color: #333;  /* Darker text to match the name */
  line-height: 1.5;
  margin-bottom: 20px;
  text-align: left;  /* Left-align the bio text */
}

.mission-statement {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-top: 20px;
}

.mission-statement h3 {
  margin: 0 0 12px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.mission-statement p {
  margin: 0;
  color: #666;
  line-height: 1.5;
}

/* Tags Section */
.profile-tags {
  width: 300px;  /* Match image width */
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;  /* Align tags to left instead of center */
  gap: 8px;
}

.tag-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  gap: 6px;
  width: 100%;
  justify-content: flex-start;
}

.commission-indicator-container {
  display: flex;
  align-items: center;
}

.commission-badge {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  color: white;
}

.commission-badge.scientist-theme {
  background: #6566be;
}

.commission-badge.artist-theme {
  background: #f8b19b;
}

.commission-badge.both-theme {
  background: #d1d2e6;
}

.tag-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.expertise-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-family: var(--font-primary);
  border: var(--stdOutline) solid var(--borderColor);
  background: var(--bckColor);
  color: #555;
  white-space: nowrap;
}

/* Science-themed tags - matching FilterDropdown styling */
.expertise-tag.scientist-theme {
  background: var(--bckColor);
  border-color: var(--sciOutlineColor);
  color: #555;
}

/* Art-themed tags - matching FilterDropdown styling */
.expertise-tag.artist-theme {
  background: var(--bckColor);
  border-color: var(--artOutlineColor);
  color: #555;
}

/* Neutral-themed tags - matching FilterDropdown styling */
.expertise-tag.both-theme {
  background: var(--bckColor);
  border-color: var(--borderColor);
  color: var(--textColor);
}

/* Tags are display-only, no hover effects needed */

/* Social Links Section */
.social-links-section {
  margin-top: 20px;
}

.social-links-section h3 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.social-links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.social-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: white;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  text-decoration: none;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.social-link:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: var(--artColor);
}

.social-link svg {
  color: #666;
  transition: color 0.2s ease;
}

.social-link:hover svg {
  color: var(--artColor);
}

/* Publications Inline Section (for scientists) */
.publications-inline-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e1e5e9;
}

.publications-inline-section h3 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.publications-inline-section.scientist-section h3 {
  color: #6566be;
}

.publications-inline-section.artist-section h3 {
  color: var(--artColor);
}

.publications-text {
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  margin: 0;
}

/* Section Divider */
.section-divider {
  height: 1px;
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  margin: 25px 40px 5px 40px;
  opacity: 0.5;
}

/* Section Heading */
.section-heading {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
}

/* Portfolio Section */
.portfolio-section {
  background: white;
  padding: 5px 40px 40px;
  border-radius: 12px;
  margin-bottom: 40px;
}

.portfolio-header {
  margin-bottom: 15px;
}

.portfolio-header .section-heading {
  margin-bottom: 8px;
}

.portfolio-description {
  color: #666;
  font-size: 16px;
  line-height: 1.5;
}

.portfolio-showcase {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.portfolio-main {
  display: flex;
  gap: 24px;
  align-items: stretch;
}

.portfolio-image-section {
  width: 600px;
  flex-shrink: 0;
}

.portfolio-main-image {
  position: relative;
  width: 100%;
  height: 400px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 0;
  overflow: hidden;
}

/* Portfolio Thumbnails Wrapper - centers thumbnails with arrows */
.portfolio-thumbnails-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
}

/* Carousel Navigation - Grey themed arrows */
.carousel-nav {
  background: #f5f5f5;
  border: 1px solid #ddd;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.carousel-nav:hover {
  background: #e8e8e8;
  border-color: #ccc;
}

.carousel-nav:active {
  background: #ddd;
}

.carousel-nav svg {
  color: #666;
  transition: color 0.2s ease;
}

.carousel-nav:hover svg {
  color: #333;
}

/* Image Counter - Subtle page indicator */
.image-counter {
  align-self: flex-end;
  color: #999;
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0.5px;
  font-variant-numeric: tabular-nums;
}

.portfolio-details {
  flex: 1;
  min-width: 200px;
  max-width: 500px;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.portfolio-title {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
}

.portfolio-text .portfolio-description {
  color: #666;
  line-height: 1.6;
  font-size: 16px;
}

.portfolio-thumbnails {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 8px 4px;
  max-width: calc(100% - 120px); /* Leave room for arrows */
  justify-content: center;
  flex-wrap: wrap;
}

.portfolio-thumbnail {
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: opacity 0.2s ease, border-color 0.2s ease;
  border: 2px solid transparent;
  opacity: 0.6;
}

.portfolio-thumbnail:hover {
  opacity: 0.85;
}

.portfolio-thumbnail.active {
  border-color: #ccc;
  opacity: 1;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
}

/* Additional Sections */
.publications-section {
  background: white;
  padding: 40px;
  border-radius: 12px;
  margin-bottom: 40px;
}

.publications-content {
  color: #666;
  line-height: 1.6;
  font-size: 16px;
}

.publications-content p {
  margin: 0;
}

/* Placeholder text styling */
.placeholder-text {
  color: #999;
  font-style: italic;
  font-size: 14px;
  line-height: 1.4;
}

.portfolio-placeholder,
.publications-placeholder {
  padding: 40px 20px;
  text-align: center;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #ddd;
}

.profile-tags h4 {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
  text-align: left;
}

/* Responsive Design */
@media (max-width: 768px) {
  .profile-content {
    padding: 20px;
  }
  
  .profile-main {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .profile-image-section {
    justify-self: center;
  }
  
  .profile-info {
    text-align: center;
    padding-top: 0;
  }
  
  .profile-tags {
    align-items: center;
  }
  .portfolio-main {
    flex-direction: column;
    gap: 16px;
  }

  .portfolio-image-section {
    width: 100%;
  }

  .portfolio-details {
    min-width: auto;
    max-width: none;
    padding: 0;
    gap: 12px;
  }

  .section-divider {
    margin: 20px 20px;
  }
  
  .portfolio-section,
  .publications-section {
    padding: 20px;
  }
}

.back-btn {
  padding: 12px 24px;
  background: #6566be;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #5555a8;
  transform: translateY(-1px);
}

/* Accessibility Styles */
.skip-link {
  position: absolute;
  top: -40px;
  left: 6px;
  background: var(--sciColor);
  color: white;
  padding: 8px;
  text-decoration: none;
  border-radius: 0 0 4px 4px;
  z-index: 1000;
  font-weight: 500;
  transition: top 0.3s ease;
}

.skip-link:focus {
  top: 0;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Focus Management */
.back-to-gallery-btn:focus,
.social-link:focus,
.carousel-nav:focus,
.portfolio-thumbnail:focus,
.back-btn:focus {
  outline: 2px solid var(--sciColor);
  outline-offset: 2px;
}

/* Portfolio thumbnail button styles */
.portfolio-thumbnail {
  background: none;
  border: 2px solid transparent;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.2s ease, border-color 0.2s ease;
  border-radius: 4px;
  overflow: hidden;
}

.portfolio-thumbnail.active {
  border-color: #ccc;
}

.portfolio-thumbnail:focus {
  outline: 2px solid #999;
  outline-offset: 2px;
}

/* Touch Target Optimization */
@media (max-width: 768px) {
  .back-to-gallery-btn,
  .social-link,
  .portfolio-thumbnail,
  .back-btn {
    min-height: 44px;
    min-width: 44px;
    padding: 12px;
  }

  .social-link {
    padding: 10px 14px;
  }

  /* Carousel responsive adjustments */
  .portfolio-thumbnails-wrapper {
    gap: 10px;
  }

  .carousel-nav {
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    padding: 0;
  }

  .portfolio-thumbnails {
    max-width: calc(100% - 100px);
  }

  .portfolio-thumbnail {
    width: 60px;
    height: 60px;
    min-width: 60px;
    min-height: 60px;
    padding: 0;
  }
}

/* High Contrast Mode Support */
@media (prefers-contrast: high) {
  .carousel-nav,
  .back-to-gallery-btn,
  .social-link,
  .back-btn {
    border: 1px solid ButtonText;
  }

  .commission-dot {
    border: 1px solid ButtonText;
  }
}

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
  *,
  ::before,
  ::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }

  .commission-dot {
    animation: none;
  }
}

/* Keyboard Navigation Support for Carousel */
.portfolio-thumbnails:focus-within .portfolio-thumbnail:not(:focus) {
  opacity: 0.6;
}

/* Better focus indicators for high visibility */
@media (prefers-reduced-motion: no-preference) {
  .skip-link:focus,
  .carousel-nav:focus,
  .portfolio-thumbnail:focus {
    animation: focusPulse 0.6s ease-in-out;
  }
}

@keyframes focusPulse {
  0% { box-shadow: 0 0 0 0 rgba(101, 102, 190, 0.7); }
  70% { box-shadow: 0 0 0 4px rgba(101, 102, 190, 0); }
  100% { box-shadow: 0 0 0 0 rgba(101, 102, 190, 0); }
}
</style>